import { AudioCapture } from "./AudioCapture";
import { SpeechRecognizer } from "./SpeechRecognizer";
import {
  containsSpeech,
  speechDurationMs,
  MAX_CLIP_DURATION_MS,
  MIN_SPEECH_DURATION_MS,
} from "./vad";
import { looksLikeHallucination } from "./hallucinationFilter";
import { interpret, InterpretHttpDeps } from "./interpretClient";
import {
  transcribe,
  AudioTooLargeError,
  UnsupportedAudioError,
  TranscribeHttpDeps,
} from "./transcribeClient";
import {
  mapInterpretResponse,
  applyCandidateSelections,
  DEFAULT_CONFIDENCE_THRESHOLD,
} from "./criteriaMapper";
import {
  AudioClip,
  InterpretResponse,
  MappedCriteria,
  ReasonCode,
  VoiceCriteria,
  VoiceState,
} from "./types";
import { logVoicePerf } from "./perf";

const TRAILING_SILENCE_AUTOSTOP_MS = 400;
// Below this RMS a level callback counts as silence. Higher than vad.ts's
// SILENCE_RMS_THRESHOLD on purpose: this one decides when to STOP recording,
// so it has to ignore room tone that the offline gate is happy to keep.
const SPEECH_RMS_THRESHOLD = 0.025;
const INTERPRET_TIMEOUT_MS = 20000;
// On-device path only. Clips are capped at MAX_CLIP_DURATION_MS; wasm whisper
// runs slower than realtime on low-end hardware, so allow several times the
// clip length before giving up rather than cutting off a transcription still
// running.
const TRANSCRIBE_TIMEOUT_MS = 45000;
// Server path. Covers the upload, the speech-to-text pass and the
// interpretation, because /Voice/Transcribe does all three in one request.
const SERVER_TRANSCRIBE_TIMEOUT_MS = 30000;

export class TimeoutError extends Error {}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new TimeoutError(`timed out after ${ms}ms`)), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        clearTimeout(timer);
        reject(err);
      },
    );
  });
}

/** axios-client tags offline/DNS failures rather than surfacing a status code. */
function isNetworkError(err: unknown): boolean {
  return Boolean(
    err && typeof err === "object" && (err as { isNetworkError?: boolean }).isNetworkError,
  );
}

export type VoiceMachineDeps = {
  audioCapture: AudioCapture;
  /**
   * On-device transcription. Omitted on web, where /Voice/Transcribe does the
   * speech-to-text server-side; a native build still supplies one. When this
   * is absent the machine takes the server path and never shows a model-load
   * step, because there is no model to load.
   */
  recognizer?: SpeechRecognizer;
  http: InterpretHttpDeps;
  /** Required whenever `recognizer` is omitted — it is how the audio reaches the server. */
  transcribeHttp?: TranscribeHttpDeps;
  getExistingCriteria: () => Partial<VoiceCriteria>;
  languageHint: () => string;
  clientVersion: string;
  /** Function, not a static value: Vocabulary loads asynchronously after the machine is constructed. */
  getConfidenceThreshold?: () => number | undefined;
  /** Fed to an on-device recogniser as initialPrompt. Unused on the server path — /Voice/Transcribe takes no prompt field. */
  getInitialPrompt?: () => string | undefined;
  /** Server truncates here too (hard-rejects over 1000) — trim client-side so the user sees what actually gets sent. */
  getMaxTranscriptChars?: () => number | undefined;
};

export type VoiceMachineListener = (state: VoiceState) => void;

/**
 * Owns every decision the voice-search feature makes: permission handling,
 * model prep, the listen/auto-stop timer, VAD + hallucination gating,
 * calling the server, mapping the result, and every state transition in
 * between. Nothing here touches window/document/fetch/whisper directly —
 * those are all injected via VoiceMachineDeps.
 */
export class VoiceMachine {
  private state: VoiceState = { name: "idle" };
  private listeners = new Set<VoiceMachineListener>();
  private levelMeterInterval: ReturnType<typeof setInterval> | null = null;
  private silenceTimer: ReturnType<typeof setTimeout> | null = null;
  private elapsedStart = 0;
  private lastVoiceAt = 0;
  /**
   * Trailing-silence auto-stop stays disarmed until the mic actually hears
   * something. Without this the lead-in pause before the user starts talking
   * counts as trailing silence and recording stops before a word is said —
   * latent at 1500ms, near-certain at 400ms. The gate is the fix, not the
   * timeout value.
   */
  private hasDetectedSpeech = false;
  /** Loudest frame seen this run — logged so the threshold can be tuned from data. */
  private peakRms = 0;
  private abortController: AbortController | null = null;
  /** Kept from the on-device pass so confirmTranscript() can still send them after the confirm step. */
  private pendingConfidence: number | null = null;
  private pendingModelId = "";
  /**
   * Server path only. /Voice/Transcribe already returned the interpretation
   * alongside the transcript, so accepting that transcript unchanged can go
   * straight to review — the second round-trip is paid only when the user
   * edits the text, which invalidates everything the server derived from it.
   */
  private pendingResponse: InterpretResponse | null = null;

  constructor(private deps: VoiceMachineDeps) {}

  subscribe(listener: VoiceMachineListener): () => void {
    this.listeners.add(listener);
    listener(this.state);
    return () => this.listeners.delete(listener);
  }

  getState(): VoiceState {
    return this.state;
  }

  private setState(next: VoiceState) {
    this.state = next;
    this.listeners.forEach((l) => l(next));
  }

  async start(): Promise<void> {
    this.clearTimers();
    this.pendingResponse = null;
    this.setState({ name: "permissionPrompt" });

    const permission = await this.deps.audioCapture.requestPermission();
    if (permission === "denied") {
      this.setState({ name: "denied" });
      return;
    }
    if (permission === "unavailable") {
      this.setState({ name: "failed", reasonCode: "UNSUPPORTED_BROWSER" });
      return;
    }

    const recognizer = this.deps.recognizer;
    if (recognizer && !recognizer.isReady()) {
      this.setState({ name: "modelLoading", progress: 0 });
      try {
        await recognizer.prepare((fraction) => {
          if (this.state.name === "modelLoading") {
            this.setState({ name: "modelLoading", progress: fraction });
          }
        });
      } catch (err) {
        // Never log the transcript, but the failure reason itself is safe
        // and is the only way to tell "no vendor build" apart from "vendor
        // build present but init()/transcribe() throws" from the console.
        console.error("[voice] recognizer.prepare() failed:", err);
        this.setState({ name: "modelFailed", reasonCode: "MODEL_UNAVAILABLE" });
        return;
      }
    }

    try {
      this.elapsedStart = Date.now();
      this.lastVoiceAt = Date.now();
      this.hasDetectedSpeech = false;
      this.peakRms = 0;
      await this.deps.audioCapture.start((rms) => this.onLevel(rms));
    } catch (err) {
      console.error("[voice] audioCapture.start() failed:", err);
      this.setState({ name: "failed", reasonCode: "MIC_IN_USE" });
      return;
    }

    this.setState({ name: "listening", elapsedMs: 0, level: 0 });
    this.levelMeterInterval = setInterval(() => {
      if (this.state.name !== "listening") return;
      const elapsedMs = Date.now() - this.elapsedStart;
      this.setState({ ...this.state, elapsedMs });
      if (elapsedMs >= MAX_CLIP_DURATION_MS) {
        logVoicePerf("vad.autostop", {
          reason: "maxClipDuration",
          listeningMs: elapsedMs,
          heardSpeech: this.hasDetectedSpeech,
          peakRms: Number(this.peakRms.toFixed(4)),
        });
        void this.stop();
      }
    }, 200);
  }

  private onLevel(rms: number) {
    if (this.state.name !== "listening") return;
    this.setState({ ...this.state, level: rms });

    if (rms > this.peakRms) this.peakRms = rms;

    // Auto-stop on trailing silence, in addition to the explicit Stop button.
    // "Trailing" is the operative word: this only arms once speech has been
    // heard, so waiting for the user to begin can never end the recording.
    if (rms >= SPEECH_RMS_THRESHOLD) {
      this.hasDetectedSpeech = true;
      this.lastVoiceAt = Date.now();
      if (this.silenceTimer) {
        clearTimeout(this.silenceTimer);
        this.silenceTimer = null;
      }
    } else if (this.hasDetectedSpeech && !this.silenceTimer) {
      this.silenceTimer = setTimeout(() => {
        const silenceMs = Date.now() - this.lastVoiceAt;
        if (silenceMs >= TRAILING_SILENCE_AUTOSTOP_MS) {
          logVoicePerf("vad.autostop", {
            reason: "trailingSilence",
            silenceMs,
            listeningMs: Date.now() - this.elapsedStart,
            rmsThreshold: SPEECH_RMS_THRESHOLD,
            peakRms: Number(this.peakRms.toFixed(4)),
          });
          void this.stop();
        }
      }, TRAILING_SILENCE_AUTOSTOP_MS);
    }
  }

  /** Stop = "process what you heard." */
  async stop(): Promise<void> {
    if (this.state.name !== "listening") return;
    this.clearTimers();

    const clip = await this.deps.audioCapture.stop();
    this.setState({ name: "transcribing" });

    // Runs before anything leaves the device. On the server path this is also
    // what stops a clip of room tone from becoming a billed transcription.
    if (!containsSpeech(clip.pcm16k)) {
      // Distinguishes "stopped too early" from "mic too quiet to clear the
      // threshold" — the two produce the same NO_SPEECH_DETECTED toast.
      logVoicePerf("vad.rejected", {
        reason: "NO_SPEECH_DETECTED",
        clipMs: Math.round(clip.durationMs),
        speechMs: Math.round(speechDurationMs(clip.pcm16k)),
        minSpeechMs: MIN_SPEECH_DURATION_MS,
        peakRms: Number(this.peakRms.toFixed(4)),
        heardSpeech: this.hasDetectedSpeech,
      });
      this.setState({ name: "failed", reasonCode: "NO_SPEECH_DETECTED" });
      return;
    }

    if (this.deps.recognizer) {
      await this.transcribeOnDevice(clip);
    } else {
      await this.transcribeOnServer(clip);
    }
  }

  /** Server path: one multipart request that transcribes AND interprets. */
  private async transcribeOnServer(clip: AudioClip): Promise<void> {
    const transcribeHttp = this.deps.transcribeHttp;
    if (!transcribeHttp) {
      console.error("[voice] no recognizer and no transcribeHttp — nothing can transcribe this clip");
      this.setState({ name: "failed", reasonCode: "UNSUPPORTED_BROWSER" });
      return;
    }

    const controller = new AbortController();
    this.abortController = controller;
    const startedAt = Date.now();

    let response;
    try {
      response = await withTimeout(
        transcribe(transcribeHttp, {
          clip,
          languageHint: this.deps.languageHint(),
          existingCriteria: this.deps.getExistingCriteria(),
          signal: controller.signal,
        }),
        SERVER_TRANSCRIBE_TIMEOUT_MS,
      );
    } catch (err) {
      if (err instanceof TimeoutError) controller.abort();
      // cancel() already moved us on and aborted this pass — its rejection
      // is the expected outcome, not a failure to show the user.
      if (this.getState().name !== "transcribing") return;

      logVoicePerf("transcribe.roundTrip", {
        ms: Date.now() - startedAt,
        failed: err instanceof TimeoutError ? "timeout" : "error",
        uploadBytes: clip.encoded?.blob.size ?? null,
      });
      console.error("[voice] /Voice/Transcribe failed:", err);
      this.setState({ name: "failed", reasonCode: serverTranscribeReason(err) });
      return;
    }

    if (this.getState().name !== "transcribing") return;

    logVoicePerf("transcribe.roundTrip", {
      ms: Date.now() - startedAt,
      uploadBytes: clip.encoded?.blob.size ?? null,
      uploadMime: clip.encoded?.mimeType ?? "audio/wav",
      clipMs: Math.round(clip.durationMs),
      resolvedVia: response.resolvedVia,
      reasonCode: response.reasonCode,
    });

    const text = (response.transcript ?? "").trim();
    if (!text || looksLikeHallucination(text)) {
      this.setState({ name: "failed", reasonCode: "NO_SPEECH_DETECTED" });
      return;
    }

    // Confidence has no meaning on this path: the endpoint reports none, and
    // re-interpreting an edit is the only thing that would have used it.
    this.pendingConfidence = null;
    this.pendingModelId = "";
    this.pendingResponse = response;
    this.setState({ name: "confirm", transcript: this.capTranscript(text) });
  }

  /** On-device path: transcribe locally, then interpret in a separate call. */
  private async transcribeOnDevice(clip: AudioClip): Promise<void> {
    const recognizer = this.deps.recognizer;
    if (!recognizer) return;

    const controller = new AbortController();
    this.abortController = controller;
    let transcript;
    try {
      transcript = await withTimeout(
        recognizer.transcribe(clip, {
          initialPrompt: this.deps.getInitialPrompt?.(),
          signal: controller.signal,
          // Multilingual model: hint the browser locale rather than pinning
          // English, and let whisper detect when there is no usable hint.
          language: this.deps.languageHint() || "auto",
        }),
        TRANSCRIBE_TIMEOUT_MS,
      );
    } catch (err) {
      if (err instanceof TimeoutError) controller.abort();
      if (this.getState().name !== "transcribing") return;
      console.error("[voice] recognizer.transcribe() failed:", err);
      this.setState({
        name: "failed",
        reasonCode: err instanceof TimeoutError ? "TIMEOUT" : "UNKNOWN",
      });
      return;
    }

    if (this.getState().name !== "transcribing") return;

    if (!transcript.text.trim() || looksLikeHallucination(transcript.text)) {
      this.setState({ name: "failed", reasonCode: "NO_SPEECH_DETECTED" });
      return;
    }

    // Stop short of the server: the user reads (and may correct) what the
    // recogniser heard first, and confirmTranscript() carries it on from here.
    this.pendingConfidence = transcript.meanTokenProb;
    this.pendingModelId = transcript.modelId;
    this.pendingResponse = null;
    this.setState({ name: "confirm", transcript: this.capTranscript(transcript.text) });
  }

  private capTranscript(text: string): string {
    const max = this.deps.getMaxTranscriptChars?.();
    return max ? text.slice(0, max) : text;
  }

  /**
   * User pressed the check on the confirm screen. `text` is what is on
   * screen — the transcript, or the user's edit of it.
   *
   * On the server path the interpretation of the *unedited* transcript is
   * already in hand from /Voice/Transcribe, so accepting it as-is applies it
   * with no further request. An edit means the server interpreted text the
   * user has since rejected, so that result is dropped and /Voice/Interpret
   * is called with what they actually typed.
   */
  async confirmTranscript(text?: string): Promise<void> {
    if (this.state.name !== "confirm") return;

    const raw = (text ?? this.state.transcript).trim();
    const transcriptText = this.capTranscript(raw);

    if (!transcriptText) {
      this.setState({ name: "failed", reasonCode: "NO_SPEECH_DETECTED" });
      return;
    }

    // An edit is the user's own text, not something the recogniser scored —
    // don't pass off its confidence as covering it.
    const edited = transcriptText !== this.state.transcript;

    if (!edited && this.pendingResponse) {
      const response = this.pendingResponse;
      this.pendingResponse = null;
      this.setState({ name: "interpreting" });
      this.applyInterpretResponse(response, transcriptText);
      return;
    }
    this.pendingResponse = null;

    this.setState({ name: "interpreting" });
    const interpretStartedAt = Date.now();
    try {
      // Design for ~4s (fast path ~1.3s, LLM fallback ~3.7s), tolerate a
      // ~15s cold start — 20s client timeout, then fall back to manual entry.
      const response = await withTimeout(
        interpret(this.deps.http, {
          transcript: transcriptText,
          sttConfidence: edited ? null : this.pendingConfidence,
          sttModel: this.pendingModelId,
          clientVersion: this.deps.clientVersion,
          languageHint: this.deps.languageHint(),
          existingCriteria: this.deps.getExistingCriteria(),
        }),
        INTERPRET_TIMEOUT_MS,
      );

      logVoicePerf("interpret.roundTrip", {
        ms: Date.now() - interpretStartedAt,
        resolvedVia: response.resolvedVia,
        reasonCode: response.reasonCode,
      });

      this.applyInterpretResponse(response, transcriptText);
    } catch (err) {
      logVoicePerf("interpret.roundTrip", {
        ms: Date.now() - interpretStartedAt,
        failed: err instanceof TimeoutError ? "timeout" : "error",
      });
      console.error("[voice] interpret() failed:", err);
      this.setState({
        name: "failed",
        reasonCode: err instanceof TimeoutError ? "TIMEOUT" : "SERVER_ERROR",
      });
    }
  }

  /**
   * Turns an interpretation into the next state. Shared by both paths: what
   * /Voice/Transcribe returns and what /Voice/Interpret returns are the same
   * shape and get the same treatment.
   */
  private applyInterpretResponse(response: InterpretResponse, transcriptText: string): void {
    const mapped = mapInterpretResponse(
      response,
      this.deps.getConfidenceThreshold?.() ?? DEFAULT_CONFIDENCE_THRESHOLD,
    );

    // reasonCode is an outcome, not always an error — but everything
    // except a clean extraction or a genuine ambiguity is a dead end for
    // this attempt, so route those straight to `failed` instead of
    // showing a review screen with a mostly-empty form.
    if (mapped.reasonCode === "AMBIGUOUS_AIRPORT") {
      if (mapped.candidates?.length) {
        this.setState({
          name: "needsInput",
          candidates: mapped.candidates,
          partial: mapped,
          transcript: transcriptText,
        });
      } else {
        this.setState({ name: "failed", reasonCode: "UNKNOWN" });
      }
      return;
    }
    if (mapped.reasonCode) {
      this.setState({ name: "failed", reasonCode: mapped.reasonCode });
      return;
    }

    this.setState({ name: "review", result: mapped, transcript: transcriptText });
  }

  /** Discard the confirm screen without interpreting anything. */
  discardTranscript(): void {
    if (this.state.name !== "confirm") return;
    this.pendingResponse = null;
    this.setState({ name: "idle" });
  }

  /** Cancel = "throw it away, leave my form alone." Valid while listening. */
  cancel(): void {
    this.clearTimers();
    this.abortController?.abort();
    this.pendingResponse = null;
    this.deps.audioCapture.cancel();
    this.setState({ name: "idle" });
  }

  /** Discard a transcript/review result the user doesn't want applied. */
  discardReview(): void {
    this.setState({ name: "idle" });
  }

  /**
   * User picked an airport per ambiguous slot in the needsInput chooser.
   * Client-side merge only, no second server round-trip — goes straight to
   * review with the picks applied.
   */
  resolveAmbiguity(selections: Partial<Record<keyof VoiceCriteria, string>>): void {
    if (this.state.name !== "needsInput") return;
    const result = applyCandidateSelections(this.state.partial, selections);
    this.setState({ name: "review", result, transcript: this.state.transcript });
  }

  reset(): void {
    this.clearTimers();
    this.pendingResponse = null;
    this.setState({ name: "idle" });
  }

  fail(reasonCode: ReasonCode): void {
    this.setState({ name: "failed", reasonCode });
  }

  private clearTimers() {
    if (this.levelMeterInterval) clearInterval(this.levelMeterInterval);
    if (this.silenceTimer) clearTimeout(this.silenceTimer);
    this.levelMeterInterval = null;
    this.silenceTimer = null;
  }
}

function serverTranscribeReason(err: unknown): ReasonCode {
  if (err instanceof TimeoutError) return "TIMEOUT";
  if (err instanceof AudioTooLargeError) return "AUDIO_TOO_LARGE";
  if (err instanceof UnsupportedAudioError) return "NO_SPEECH_DETECTED";
  if (isNetworkError(err)) return "OFFLINE";
  return "SERVER_ERROR";
}

export type { MappedCriteria };
